<template>
  <form-block
    v-model="isEnabled"
    :title="title !== null ? title : tr('Advantage campaign budget')"
    :description="description !== null ? description : tr('If you enable this feature, it will automatically distribute your campaign`s budget across different ad sets to get more results depending on your delivery optimization choices and bid strategy. The purpose of this is to ensure the best overall result, so the budget may not be evenly distributed among the individual ad sets. Your posts, so to speak, will `compete` with each other. For example, if you have 2 active ad sets in your campaign, it`s possible that the majority of your budget will be allocated to one ad set if it achieves the best overall performance.')"
    :disabled="disabled || !entity.isNew"
    :switchable="switchable"
  >
    <p v-if="!hasSelectedAdAccount" class="m-0 text-muted-foreground">
      {{ tr('Please, select a brand.') }}
    </p>
    <budget-editor
      v-else
      ref="budgetEditor"
      v-model="entity.output_campaign.budget"
      :label="minimal ? '' : tr('Campaign budget')"
      :period="entity.output_campaign.budget_period"
      level="campaign"
      :currency-code="entity.adAccountCurrency ? entity.adAccountCurrency.code : 'EUR'"
      :ad-account-currency-symbol="entity.adAccountCurrency ? entity.adAccountCurrency.symbol : null"
      :ad-account-id="entity.ad_account ? entity.ad_account.id : null"
      :min-daily-budget="entity.adAccountMinDailyBugdet"
      :disabled="disabled"
      :period-disabled="disabled || !entity.isNew"
      :show-available-ads="true"
      :show-period="!minimal"
      :lifetime-start-time="entity.settings.adset_start_time"
      :lifetime-end-time="entity.settings.adset_end_time"
      :lifetime-lifespan-max-days="entity.settings.adset_lifespan_max_days"
      @update:period="val => entity.output_campaign.budget_period = val"
      @blur="onBudgetBlur"
    >
      <template #after-input>
        <transition-expand>
          <div v-if="isEnabled && budgetPeriod === 'lifetime'" class="pt-2">
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="flex flex-col gap-1.5">
                <ui-label>{{ tr('Start date') }}</ui-label>
                <date-time-input
                  v-model="entity.settings.adset_start_time"
                  :disabled="disabled || !entity.isNew"
                  :min-date="new Date()"
                />
              </div>
              <div class="flex flex-col gap-1.5">
                <ui-label>{{ tr('End date') }}</ui-label>
                <date-time-input
                  v-model="entity.settings.adset_end_time"
                  :disabled="disabled || !entity.isNew"
                  :min-date="new Date()"
                />
              </div>
            </div>
          </div>
        </transition-expand>
      </template>

      <template #info-texts="{ invalidBudgetInfoText, isBudgetRequestRunning, minBudgetInfoText }">
        <transition-expand>
          <info-text v-if="originalCampaignBudget && originalCampaignBudget > entity.output_campaign.budget">
            {{ tr('Reducing the campaign budget may result in the suspension of some already running ads.') }}
          </info-text>
        </transition-expand>

        <transition-expand>
          <info-text v-if="invalidLifetimeRange" type="error">
            {{ invalidLifetimeRangeInfoText }}
          </info-text>
          <info-text v-else-if="hasPastLifetimeRange()" type="error">
            {{ pastLifetimeRangeInfoText() }}
          </info-text>
          <info-text v-else-if="shouldShowBudgetError" type="error">
            <div v-html="invalidBudgetInfoText" />
          </info-text>
          <info-text v-else :icon="isBudgetRequestRunning ? 'loader' : null">
            {{ minBudgetInfoText }}
          </info-text>
        </transition-expand>
      </template>
    </budget-editor>

    <div v-if="!minimal && hasSelectedAdAccount" class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Bid strategy') }}</ui-label>
      <select-field
        v-model="entity.output_campaign.bid_strategy"
        :options="facebookCampaignBidStrategies"
        :reduce="(obj) => obj.machine_name"
        :aria-label="tr('Bid strategy')"
        :disabled="disabled || !entity.isNew"
      />
    </div>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import BudgetBlockMixin from '@/mixins/RuleForm/Blocks/Budget.js';
import BudgetEditor from '@/ui/components/BudgetEditor.vue';
import DateTimeInput from '@/ui/components/DateTimeInput.vue';
import InfoText from '@/ui/components/InfoText.vue';
import SelectField from '@/ui/components/SelectField.vue';
import TransitionExpand from '@/ui/components/TransitionExpand.vue';

export default {
  name: 'CampaignBudgetBlock',
  components: {
    BudgetEditor,
    DateTimeInput,
    InfoText,
    SelectField,
    TransitionExpand,
  },
  mixins: [ RuleFormBlockMixin, BudgetBlockMixin ],
  props: {
    title: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    description: {
      type: [ String, null ],
      required: false,
      default: () => null,
    },
    switchable: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    minimal: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      isEnabled: !this.switchable || !this.entity.settings.adset_budget,
      originalCampaignBudget: !this.entity.isNew ? this.entity.output_campaign.budget : null,
    };
  },
  computed: {
    isCampaignLevel() {
      return this.isEnabled;
    },
    isAdsetLevel() {
      return !this.isCampaignLevel;
    },
    hasSelectedAdAccount() {
      return this.entity.ad_account && this.entity.ad_account.id;
    },
    currencyDetails() {
      return this.$store.getters['app/currencies'].find(i => i.code === this.entity.adAccountCurrency.code);
    },
    facebookCampaignBidStrategies() {
      const list = this.$store.getters['app/facebookCampaignBidStrategies'];
      return this.conditionalFilteredList(list);
    },
  },
  watch: {
    isEnabled: {
      immediate: true,
      handler(val) {
        if (val) {
          this.entity.settings.adset_budget = null;
          this.entity.settings.adset_bid_strategy = null;

          if (this.entity.output_campaign.bid_strategy === null) {
            this.entity.output_campaign.budget_period = this.budgetPeriods[0].machine_name;
            this.entity.output_campaign.bid_strategy = this.facebookCampaignBidStrategies[0].machine_name;
          }
        }
        else {
          this.entity.output_campaign.budget = null;
          this.entity.output_campaign.budget_period = null;
          this.entity.output_campaign.bid_strategy = null;
        }
      },
    },
    'entity.output_campaign.budget'() {
      this.entity.settings.adset_budget = null;
      this.entity.settings.adset_budget_period = null;
      this.entity.settings.max_active_ads = null;
    },
  },
  methods: {
    validate() {
      if (this.isEnabled) {
        return this.validateBudget();
      }

      return true;
    },
  },
};
</script>
